import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { fetchPatientsGetById } from '~/backend/connector-next-api/connector-next-components';
import {
    DownloadFailedError,
    downloadInstancesToFiles,
    isAbortError,
} from '~/backend/connector-next-api/dataRetrieval';
import {
    connectorViewerOpenPhaseState,
    type ConnectorViewerOpenPhase,
} from '~/state/atoms/dicomProcessorAtoms';
import { patientViewerPath } from '~/routes/paths';
import { buildConnectorManifest } from './buildConnectorManifest';
import { necessaryDatasetKeys } from './necessaryDatasets';
import {
    openSelection,
    resolveOpenIntent,
    type OpenIntent,
    type OpenRefusal,
} from './resolveOpenIntent';
import type { OpenInViewer } from './openInViewerContext';
import { pendingViewerOpenState } from './pendingViewerOpenAtom';
import { resolveOpenTargets } from './resolveOpenTargets';
import {
    allInstancesOf,
    partitionPlansByAnchor,
    planProgressiveOpen,
    type OpenDownloadPlan,
} from './streamingPlan';
import {
    useIncompleteRetrievalDecision,
    type IncompleteRetrievalState,
} from './useIncompleteRetrievalDecision';
import { useOpenNotify } from './useOpenNotify';

export type ConnectorViewerOpenProgress = {
    /** Instances settled (downloaded or skipped). */
    settled: number;
    total: number;
};

export type OpenInViewerDownload = {
    open: OpenInViewer;
    phase: ConnectorViewerOpenPhase;
    progress: ConnectorViewerOpenProgress;
    cancel: () => void;
    incompleteRetrieval: IncompleteRetrievalState;
    resolveIncompleteRetrieval: (proceed: boolean) => void;
};

const ZERO_PROGRESS: ConnectorViewerOpenProgress = { settled: 0, total: 0 };

/** The notice for each reason an open is refused. */
const REFUSAL_MESSAGE: Record<OpenRefusal, string> = {
    structureSetHasNoImage: 'patientList.drilldown.cannotOpenNoImage',
    targetIsGone: 'patientList.open.targetIsGone',
};

/**
 * How long the handoff may sit unclaimed while this screen is still mounted;
 * reaching it means the navigation did not take.
 */
export const HANDOFF_TIMEOUT_MS = 5_000;

/**
 * The worklist half of "open in viewer": resolve the patient's datasets to
 * SOP instances, download the ones the viewer cannot open without, confirm a
 * short retrieval, then hand the files to the viewer and navigate.
 *
 * "Cannot open without" is every non-image dataset plus one anchor slice per
 * image series whose manifest supports progressive loading; those series'
 * remaining slices travel as a plan and are fetched from the viewer. A series
 * with no usable manifest keeps downloading in full, here, exactly as before.
 *
 * Everything that can fail on the wire happens here, before the route
 * changes, so a failure leaves the user on the list. The parse, commit and
 * render are the viewer's ({@link useViewerOpenLoader}), so the commit and
 * the load are never separated by a navigation. The phase is published in
 * {@link connectorViewerOpenPhaseState} so other DICOM entry points refuse
 * to feed the shared processor; it crosses to the viewer still held.
 */
export function useOpenInViewerDownload(): OpenInViewerDownload {
    const [phase, setPhase] = useRecoilState(connectorViewerOpenPhaseState);
    const [pending, setPending] = useRecoilState(pendingViewerOpenState);
    const navigate = useNavigate();
    const notify = useOpenNotify();

    const [progress, setProgress] =
        useState<ConnectorViewerOpenProgress>(ZERO_PROGRESS);
    const {
        state: incompleteRetrieval,
        awaitDecision: awaitIncompleteRetrievalDecision,
        settle: settleIncompleteRetrieval,
    } = useIncompleteRetrievalDecision();

    const abortRef = useRef<AbortController | null>(null);
    // Re-entry gate, written synchronously in the click handler; a second
    // Open in the same tick is refused where a Recoil read would see `idle`.
    const runningRef = useRef(false);

    const finishIdle = useCallback((): void => {
        settleIncompleteRetrieval(false);
        abortRef.current = null;
        runningRef.current = false;
        setPhase('idle');
        setProgress(ZERO_PROGRESS);
    }, [setPhase, settleIncompleteRetrieval]);

    // A handoff can only be claimed by a viewer mount; finding one while this
    // screen is mounted means the navigation did not take, so the files are
    // dropped and the pipeline released rather than held for the session.
    const reclaimAbandonedHandoff = useCallback((): void => {
        setPending(null);
        notify('patientList.open.openFailed', 'error');
        finishIdle();
    }, [setPending, notify, finishIdle]);

    useEffect(() => {
        // On mount: anything already sitting in the atom predates this screen.
        if (pending) {
            reclaimAbandonedHandoff();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const run = useCallback(
        async (
            patientId: string,
            seriesKeys: readonly string[] | 'all',
            intent: OpenIntent | undefined,
            signal: AbortSignal
        ): Promise<boolean> => {
            setPhase('resolving');
            setProgress(ZERO_PROGRESS);

            const detail = await fetchPatientsGetById(
                { pathParams: { id: patientId } },
                signal
            );
            // Resolved before the targets, because what the workflow needs is
            // what decides the retrieval. An open with no intent keeps asking
            // for the caller's selection: a plain Open still means the
            // patient, and a hand-made selection is never quietly capped.
            const resolution = resolveOpenIntent(detail, intent);
            // A refusal names no objects, so nothing below could address what
            // the user pointed at; carrying on would retrieve the whole
            // patient and let the viewer pick an image of its own. The user
            // is told here, on the list, and nothing is retrieved.
            if (resolution.unavailable !== undefined) {
                notify(REFUSAL_MESSAGE[resolution.unavailable], 'warning');
                return false;
            }
            const necessary =
                intent === undefined
                    ? undefined
                    : necessaryDatasetKeys(resolution, detail);

            const {
                instances,
                series,
                unresolved,
                receiving,
                unaddressableSlices,
            } = resolveOpenTargets(detail, necessary ?? seriesKeys);

            if (receiving > 0) {
                notify('patientList.open.seriesStillArriving', 'warning', {
                    count: receiving,
                });
            }

            if (instances.length === 0) {
                // Unaddressable datasets are not "no datasets"; report which
                // it was.
                if (unresolved > 0) {
                    notify('patientList.open.someUnresolved', 'warning', {
                        count: unresolved,
                    });
                } else {
                    notify('patientList.drilldown.emptyAll', 'warning');
                }
                return false;
            }
            if (unresolved > 0) {
                // Part of the selection cannot be addressed; the rest opens.
                notify('patientList.open.someUnresolved', 'warning', {
                    count: unresolved,
                });
            }

            // Progressive preparation: one manifest per image series decides
            // whether that series opens on a single anchor slice and streams
            // the rest. Every failure inside falls back to downloading the
            // series in full, so the worst case here is the behaviour that
            // shipped before streaming existed. Only a cancel escapes.
            const fullDownload: OpenDownloadPlan = {
                blocking: instances,
                streaming: [],
            };
            // Declared unassigned so the fallback appears once, in the catch,
            // where it states the contract: any planning failure opens the
            // series in full. `strict` enforces definite assignment.
            let plan: OpenDownloadPlan;
            try {
                plan = await planProgressiveOpen(series, { signal });
            } catch (error) {
                if (isAbortError(error) || signal.aborted) {
                    throw error;
                }
                console.debug(
                    'Progressive open planning failed; retrieving every ' +
                        'instance before opening'
                );
                plan = fullDownload;
            }

            setPhase('downloading');
            setProgress({ settled: 0, total: plan.blocking.length });
            // Per whole percent: ~100 updates however many instances.
            let lastPercent = -1;
            const { files, skipped } = await downloadInstancesToFiles(
                plan.blocking,
                {
                    signal,
                    onProgress: (settled, total) => {
                        const percent = Math.floor((settled / total) * 100);
                        if (percent === lastPercent && settled < total) {
                            return;
                        }
                        lastPercent = percent;
                        setProgress({ settled, total });
                    },
                }
            );
            // A cancel landing as the last instance settles must not hand off.
            if (signal.aborted) {
                return false;
            }

            // A series cannot stream without its anchor: `Image3D.fromManifest`
            // has no parsed slice to build from. Dropping the plan would take
            // the whole series with it — every other slice of it was addressed
            // only by the stream — so it falls back to the full download that
            // shipped before streaming existed. Doing this before the
            // confirmation is what keeps the counts honest: they then describe
            // the whole open, not the anchors alone.
            const { streamable, recover } = partitionPlansByAnchor(
                plan.streaming,
                skipped
            );
            const recovered = { files: [] as File[], skipped: 0 };
            if (recover.length > 0) {
                const addresses = recover.flatMap(allInstancesOf);
                setProgress({ settled: 0, total: addresses.length });
                const result = await downloadInstancesToFiles(addresses, {
                    signal,
                    onProgress: (settled, total) =>
                        setProgress({ settled, total }),
                });
                if (signal.aborted) {
                    return false;
                }
                recovered.files = result.files;
                // The lost anchor is counted here, in this series' own total,
                // rather than in the blocking tally it was skipped from.
                recovered.skipped = result.skipped.length;
            }

            const retrieved = [...files, ...recovered.files];
            if (retrieved.length === 0) {
                notify('patientList.open.retrieveFailed', 'error');
                return false;
            }

            // A short retrieval is put to the user before anything is handed
            // off. Slices listed without a SOP UID count the same as failed
            // downloads; the gap is identical.
            //
            // Under streaming this covers what the blocking phase retrieved
            // plus any series recovered in full above. A slice lost later,
            // while streaming, cannot be part of a decision taken before the
            // viewer opened; it leaves the image explicitly incomplete and
            // warns from the viewer instead.
            //
            // An anchor that was skipped is NOT counted: its series was
            // re-requested in full, so it is either in hand or already counted
            // among that download's own failures.
            const missing =
                skipped.length -
                recover.length +
                recovered.skipped +
                unaddressableSlices;
            if (missing > 0) {
                setPhase('confirming');
                const proceed = await awaitIncompleteRetrievalDecision(
                    retrieved.length,
                    missing
                );
                if (signal.aborted || !proceed) {
                    return false;
                }
            }

            // Hand off with the lock still held: the viewer takes ownership of
            // both, and nothing may feed the processor in between. The
            // manifest is built from the detail fetched above, so the save
            // workflow's version baselines are the ones this open loaded.
            setPhase('processing');
            setPending({
                files: retrieved,
                skipped: missing,
                streaming: streamable,
                connectorManifest: buildConnectorManifest(detail) ?? undefined,
                // Resolved against the detail this open fetched, so what the
                // viewer selects is what the list promised — including the
                // fallbacks, which are decided here rather than twice.
                intent: openSelection(resolution),
            });
            // Replace, not push: a pushed patient-list entry would let the
            // browser Back button return to the list with the patient still
            // loaded, bypassing the confirm-and-unload the way-back control
            // performs (SR-WOR-H-39-1). Leaving the viewer is the return
            // control's job, never a silent history pop.
            navigate(patientViewerPath(patientId), { replace: true });
            return true;
        },
        [
            setPhase,
            setPending,
            navigate,
            notify,
            awaitIncompleteRetrievalDecision,
        ]
    );

    const open = useCallback<OpenInViewer>(
        (patientId, seriesKeys, intent) => {
            if (runningRef.current) {
                notify('patientList.open.inFlight', 'warning');
                return;
            }
            runningRef.current = true;
            const controller = new AbortController();
            abortRef.current = controller;
            run(patientId, seriesKeys, intent, controller.signal)
                .then((handedOff) => {
                    if (handedOff) {
                        // The viewer owns the phase now; the watchdog below
                        // covers a navigation that did not take.
                        return;
                    }
                    finishIdle();
                })
                .catch((error: unknown) => {
                    if (!controller.signal.aborted && !isAbortError(error)) {
                        notify(
                            error instanceof DownloadFailedError
                                ? 'patientList.open.retrieveFailed'
                                : 'patientList.open.openFailed',
                            'error'
                        );
                    }
                    finishIdle();
                });
        },
        [run, notify, finishIdle]
    );

    // Watchdog for a handoff this screen made and then did not unmount after.
    // The normal path unmounts on the navigation and never reaches the timer.
    useEffect(() => {
        if (phase !== 'processing' || !pending) {
            return;
        }
        const timer = setTimeout(reclaimAbandonedHandoff, HANDOFF_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [phase, pending, reclaimAbandonedHandoff]);

    // A download running at unmount has nowhere to deliver; a no-op after a
    // completed handoff. A parked question settles itself on the unmount.
    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    const cancel = useCallback((): void => {
        abortRef.current?.abort();
        settleIncompleteRetrieval(false);
    }, [settleIncompleteRetrieval]);

    return {
        open,
        phase,
        progress,
        cancel,
        incompleteRetrieval,
        resolveIncompleteRetrieval: settleIncompleteRetrieval,
    };
}
